import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'
import { listStores } from './storeService'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error(sanitizeBusinessMessage(b?.message, 'Request failed'))
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type ContactMessageRow = {
  id: number
  storeId: number
  senderName: string
  senderEmail: string
  body: string
  readAt: string | null
  createdAt: string
}

export async function fetchContactUnreadCount(storeId: number): Promise<{ unreadCount: number }> {
  try {
    const { data } = await api.get(`/storebusiness/stores/${storeId}/contact-messages/unread-count`)
    return assertSuccess(data) as { unreadCount: number }
  } catch (e) {
    throw apiError(e, 'Failed to load unread count')
  }
}

/** Sum of unread counts across all stores in the organisation (for sidebar badge). */
export async function fetchTotalUnreadAcrossStores(): Promise<number> {
  const { items } = await listStores()
  if (items.length === 0) return 0
  const results = await Promise.all(
    items.map((s) =>
      fetchContactUnreadCount(s.id).catch(() => ({ unreadCount: 0 })),
    ),
  )
  return results.reduce((acc, r) => acc + (r.unreadCount ?? 0), 0)
}

export async function fetchContactMessages(
  storeId: number,
  params: { page?: number; limit?: number } = {},
): Promise<{
  items: ContactMessageRow[]
  total: number
  page: number
  limit: number
}> {
  try {
    const { data } = await api.get(`/storebusiness/stores/${storeId}/contact-messages`, {
      params: { page: params.page ?? 1, limit: params.limit ?? 25 },
    })
    return assertSuccess(data) as {
      items: ContactMessageRow[]
      total: number
      page: number
      limit: number
    }
  } catch (e) {
    throw apiError(e, 'Failed to load messages')
  }
}

export async function markContactMessageRead(storeId: number, messageId: number): Promise<{ id: number; readAt: string }> {
  try {
    const { data } = await api.patch(
      `/storebusiness/stores/${storeId}/contact-messages/${messageId}`,
      { read: true },
    )
    return assertSuccess(data) as { id: number; readAt: string }
  } catch (e) {
    throw apiError(e, 'Failed to update message')
  }
}
